import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import classNames from "classnames/bind";
import io from "socket.io-client"; // Thêm import
import styles from "./TripsManagement.module.css";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faSave,
  faSyncAlt,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_API_URL, BASE_TRIPS_URL } from "~/helper/apiService";
import Skeleton from "~/components/Skeleton/Skeleton";

const cx = classNames.bind(styles);

function TripsManagement() {
  const [trips, setTrips] = useState([]);
  const [newTripTitle, setNewTripTitle] = useState("");
  const [newTripPickupPoint, setNewTripPickupPoint] = useState("");
  const [newTripDropoffPoint, setNewTripDropoffPoint] = useState("");
  const [newTripPrice, setNewTripPrice] = useState("");
  const [newTripPhone, setNewTripPhone] = useState("");
  const [newTripDescription, setNewTripDescription] = useState("");
  const [editingTrip, setEditingTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tripsLoading, setTripsLoading] = useState(true);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  // Sorted trips: sắp xếp theo created_at mới nhất trước
  const sortedTrips = useMemo(() => {
    return [...trips].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [trips]);

  // Socket.IO connection
  useEffect(() => {
    const socket = io(BASE_API_URL); // URL backend server

    socket.on("connect", () => {
      console.log("Connected to socket:", socket.id);
    });

    socket.on("disconnect", () => {
      console.log("Disconnected from socket");
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    // Lắng nghe event thêm chuyến
    socket.on("tripAdded", (newTrip) => {
        console.log('Received tripAdded:', newTrip);
      setTrips((prev) => [...prev, newTrip]);
      showToast("Chuyến xe mới được thêm (real-time)", "success");
    });

    // Lắng nghe event sửa chuyến
    socket.on("tripUpdated", (updatedTrip) => {
      setTrips((prev) =>
        prev.map((trip) => (trip.id === updatedTrip.id ? updatedTrip : trip))
      );
      showToast("Chuyến xe được cập nhật (real-time)", "success");
    });

    // Lắng nghe event xóa chuyến
    socket.on("tripDeleted", ({ id }) => {
      const numericId = Number(id) || parseInt(id, 10); // Chuyển id thành number (xử lý cả string hoặc number)
      console.log(
        "Received tripDeleted for ID:",
        numericId,
        "Type:",
        typeof numericId
      );
      console.log("Current trips before deletion:", trips);
      setTrips((prev) => prev.filter((trip) => trip.id !== numericId));
      showToast("Chuyến xe được xóa (real-time)", "success");
    });

    // Cleanup khi unmount
    return () => {
      socket.disconnect();
    };
  }, [showToast]);

  // Fetch trips (chỉ dùng lần đầu hoặc manual reload)
  const fetchTrips = useCallback(async () => {
    try {
      setTripsLoading(true);
      const response = await fetch(`${BASE_TRIPS_URL}`, {
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        setTrips(data || []);
      } else {
        showToast(data.error || "Lỗi khi lấy thông tin chuyến xe", "error");
      }
    } catch (err) {
      showToast("Lỗi kết nối đến server: " + err.message, "error");
    } finally {
      setTripsLoading(false);
    }
  }, [showToast]);

  // Fetch trips on mount
  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  // Handle adding new trip (loại bỏ fetchTrips() sau success, socket sẽ handle)
  const handleAddTrip = useCallback(async () => {
    if (
      !newTripTitle ||
      !newTripPickupPoint ||
      !newTripDropoffPoint ||
      !newTripPrice
    ) {
      showToast(
        "Vui lòng nhập đầy đủ: tiêu đề, điểm đón, điểm trả, giá",
        "error"
      );
      return;
    }
    if (Number(newTripPrice) <= 0) {
      showToast("Giá phải lớn hơn 0", "error");
      return;
    }
    const finalPhone = newTripPhone || "0843348888"; // Mặc định nếu không nhập
    const phoneRegex = /^0\d{9}$/;
    if (!phoneRegex.test(finalPhone)) {
      showToast("Số điện thoại không hợp lệ", "error");
      return;
    }

    // Tạm thêm vào UI (sẽ được socket confirm)
    const tempId = Date.now();
    const newTripTemp = {
      id: tempId,
      title: newTripTitle,
      pickup_point: newTripPickupPoint,
      dropoff_point: newTripDropoffPoint,
      price: Number(newTripPrice),
      phone: finalPhone,
      description: newTripDescription,
      created_at: new Date().toISOString(), // Thêm created_at giả để tránh lỗi format
    };
    // setTrips((prev) => [...prev, newTripTemp]);

    try {
      setLoading(true);
      const response = await fetch(`${BASE_TRIPS_URL}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: newTripTitle,
          pickup_point: newTripPickupPoint,
          dropoff_point: newTripDropoffPoint,
          price: Number(newTripPrice),
          phone: finalPhone,
          description: newTripDescription,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        // Thay temp bằng real trip từ response (tránh duplicate từ socket)
        const realTrip = { ...data };
        delete realTrip.message; // Bỏ message không cần trong UI
        setTrips((prev) =>
          prev.map((trip) => (trip.id === tempId ? realTrip : trip))
        );
        showToast("Tạo chuyến xe thành công", "success");
        // Không fetch nữa, socket sẽ thêm real-trip cho các client khác
        setNewTripTitle("");
        setNewTripPickupPoint("");
        setNewTripDropoffPoint("");
        setNewTripPrice(0);
        setNewTripPhone(""); // Reset về rỗng để placeholder hiện
        setNewTripDescription("");
      } else {
        showToast(data.error || "Lỗi khi tạo chuyến xe", "error");
        setTrips((prev) => prev.filter((trip) => trip.id !== tempId));
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
      setTrips((prev) => prev.filter((trip) => trip.id !== tempId));
    } finally {
      setLoading(false);
    }
  }, [
    newTripTitle,
    newTripPickupPoint,
    newTripDropoffPoint,
    newTripPrice,
    newTripPhone,
    newTripDescription,
    showToast,
  ]);

  // Handle editing trip (loại bỏ fetchTrips() sau success)
  const handleEditTrip = useCallback(
    async (trip) => {
      if (
        !trip.title ||
        !trip.pickup_point ||
        !trip.dropoff_point ||
        !trip.price
      ) {
        showToast(
          "Vui lòng nhập đầy đủ: tiêu đề, điểm đón, điểm trả, giá",
          "error"
        );
        return;
      }
      if (Number(trip.price) <= 0) {
        showToast("Giá phải lớn hơn 0", "error");
        return;
      }
      const finalPhone = trip.phone || "0843348888"; // Mặc định nếu không nhập
      const phoneRegex = /^0\d{9}$/;
      if (!phoneRegex.test(finalPhone)) {
        showToast("Số điện thoại không hợp lệ", "error");
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${BASE_TRIPS_URL}${trip.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            title: trip.title,
            pickup_point: trip.pickup_point,
            dropoff_point: trip.dropoff_point,
            price: Number(trip.price),
            phone: finalPhone,
            description: trip.description,
          }),
        });
        const data = await response.json();
        if (response.ok) {
          // Cập nhật state với real data từ response (tránh duplicate từ socket)
          const realTrip = { ...data };
          delete realTrip.message; // Bỏ message không cần trong UI
          setTrips((prev) =>
            prev.map((t) => (t.id === trip.id ? realTrip : t))
          );
          showToast("Cập nhật chuyến xe thành công", "success");
          // Socket sẽ update real-time cho các client khác
          setEditingTrip(null);
        } else {
          showToast(data.error || "Lỗi khi cập nhật chuyến xe", "error");
        }
      } catch (error) {
        showToast("Lỗi kết nối: " + error.message, "error");
      } finally {
        setLoading(false);
      }
    },
    [showToast]
  );

  // Handle deleting trip (loại bỏ fetchTrips() sau success)
  const handleDeleteTrip = useCallback(
    async (id) => {
      const deletedTrip = trips.find((trip) => trip.id === id);
      setTrips((prev) => prev.filter((trip) => trip.id !== id));

      try {
        setLoading(true);
        const response = await fetch(`${BASE_TRIPS_URL}${id}`, {
          method: "DELETE",
          credentials: "include",
        });
        const data = await response.json();
        if (response.ok) {
          showToast("Xóa chuyến xe thành công", "success");
          // Socket sẽ confirm delete real-time cho các client khác
        } else {
          showToast(data.error || "Lỗi khi xóa chuyến xe", "error");
          setTrips((prev) => [...prev, deletedTrip]);
        }
      } catch (error) {
        showToast("Lỗi kết nối: " + error.message, "error");
        setTrips((prev) => [...prev, deletedTrip]);
      } finally {
        setLoading(false);
      }
    },
    [trips, showToast]
  );

  // Confirm delete trip
  const confirmDeleteTrip = useCallback(
    (trip) => {
      showModal({
        title: "Xác Nhận Xóa",
        message: `Bạn có chắc chắn muốn xóa chuyến xe ${trip.title}?`,
        onConfirm: () => handleDeleteTrip(trip.id),
        onCancel: null,
      });
    },
    [showModal, handleDeleteTrip]
  );

  // Handle reloading trip data (manual sync nếu socket miss)
  const handleReloadTrips = useCallback(async () => {
    await fetchTrips();
    showToast("Tải lại dữ liệu chuyến xe thành công", "success");
  }, [fetchTrips, showToast]);

  const truncateDescription = (desc, maxLength = 100) => {
    if (!desc) return "";
    return desc.length > maxLength
      ? `${desc.substring(0, maxLength)}...`
      : desc;
  };

  const formatPrice = (price) => {
    return Math.floor(Number(price)).toLocaleString("vi-VN") + " VND";
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const hour = date.getHours().toString().padStart(2, "0");
    const minute = date.getMinutes().toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${hour}:${minute} ${day}/${month}/${year}`;
  };

  return (
    <div className={cx("content")}>
      <div className={cx("trip-section")}>
        <div className={cx("trip-form")}>
          <h3 className={cx("section-title")}>
            {editingTrip ? "Chỉnh Sửa Chuyến Xe" : "Thêm Chuyến Xe Mới"}
          </h3>
          <div className={cx("form-row")}>
            <label className={cx("form-label")}>Tiêu Đề:</label>
            <input
              type="text"
              value={editingTrip ? editingTrip.title : newTripTitle}
              onChange={(e) =>
                editingTrip
                  ? setEditingTrip({ ...editingTrip, title: e.target.value })
                  : setNewTripTitle(e.target.value)
              }
              className={cx("input")}
              placeholder="Nhập tiêu đề chuyến xe"
            />
          </div>
          <div className={cx("form-row")}>
            <label className={cx("form-label")}>Điểm Đón:</label>
            <input
              type="text"
              value={
                editingTrip ? editingTrip.pickup_point : newTripPickupPoint
              }
              onChange={(e) =>
                editingTrip
                  ? setEditingTrip({
                      ...editingTrip,
                      pickup_point: e.target.value,
                    })
                  : setNewTripPickupPoint(e.target.value)
              }
              className={cx("input")}
              placeholder="Nhập điểm đón"
            />
          </div>
          <div className={cx("form-row")}>
            <label className={cx("form-label")}>Giá:</label>
            <input
              type="number"
              value={editingTrip ? editingTrip.price : newTripPrice}
              onChange={(e) =>
                editingTrip
                  ? setEditingTrip({ ...editingTrip, price: e.target.value })
                  : setNewTripPrice(e.target.value)
              }
              className={cx("input")}
              min="0"
              placeholder="Nhập giá"
            />
          </div>
          <div className={cx("form-row")}>
            <label className={cx("form-label")}>Điểm Trả:</label>
            <input
              type="text"
              value={
                editingTrip ? editingTrip.dropoff_point : newTripDropoffPoint
              }
              onChange={(e) =>
                editingTrip
                  ? setEditingTrip({
                      ...editingTrip,
                      dropoff_point: e.target.value,
                    })
                  : setNewTripDropoffPoint(e.target.value)
              }
              className={cx("input")}
              placeholder="Nhập điểm trả"
            />
          </div>
          <div className={cx("form-row")}>
            <label className={cx("form-label")}>Số Điện Thoại:</label>
            <input
              type="tel"
              value={editingTrip ? editingTrip.phone : newTripPhone}
              onChange={(e) =>
                editingTrip
                  ? setEditingTrip({ ...editingTrip, phone: e.target.value })
                  : setNewTripPhone(e.target.value)
              }
              className={cx("input")}
              placeholder="Mặc định là 0843348888"
            />
          </div>
          <div className={cx("form-row")}>
            <label className={cx("form-label")}>Mô Tả:</label>
            <textarea
              value={editingTrip ? editingTrip.description : newTripDescription}
              onChange={(e) =>
                editingTrip
                  ? setEditingTrip({
                      ...editingTrip,
                      description: e.target.value,
                    })
                  : setNewTripDescription(e.target.value)
              }
              className={cx("textarea")}
              placeholder="Nhập mô tả (tùy chọn)"
              rows="3"
            />
          </div>
          <div className={cx("form-actions")}>
            <button
              onClick={() =>
                editingTrip ? handleEditTrip(editingTrip) : handleAddTrip()
              }
              disabled={loading}
              className={cx("button", "save-button")}
            >
              <FontAwesomeIcon icon={faSave} className={cx("button-icon")} />
              <span className={cx("button-text")}>
                {editingTrip ? "Cập Nhật" : "Thêm Mới"}
              </span>
            </button>
            {editingTrip && (
              <button
                onClick={() => setEditingTrip(null)}
                disabled={loading}
                className={cx("button", "cancel-button")}
              >
                <FontAwesomeIcon icon={faTrash} className={cx("button-icon")} />
                <span className={cx("button-text")}>Hủy</span>
              </button>
            )}
          </div>
        </div>
        <div className={cx("trips-list")}>
          <div className={cx("list-header")}>
            <h3 className={cx("section-title")}>Danh Sách Chuyến Xe</h3>
            <div className={cx("list-actions")}>
              {tripsLoading && (
                <div style={{ width: 140 }}>
                  <Skeleton height={12} width="100%" radius={999} />
                </div>
              )}
              <button
                onClick={handleReloadTrips}
                disabled={loading}
                className={cx("button", "reload-button")}
              >
                <FontAwesomeIcon
                  icon={faSyncAlt}
                  className={cx("button-icon")}
                />
                <span className={cx("button-text")}>Tải lại</span>
              </button>
            </div>
          </div>
          {tripsLoading && sortedTrips.length === 0 ? (
            <>
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className={cx("trip-card")}>
                  <div className={cx("card-info")}>
                    <Skeleton height={14} width="35%" />
                    <Skeleton height={14} width="55%" style={{ marginTop: 10 }} />
                    <Skeleton height={14} width="70%" style={{ marginTop: 10 }} />
                    <Skeleton height={14} width="80%" style={{ marginTop: 10 }} />
                    <Skeleton height={14} width="60%" style={{ marginTop: 10 }} />
                    <Skeleton height={14} width="45%" style={{ marginTop: 10 }} />
                  </div>
                  <div className={cx("card-actions")}>
                    <Skeleton height={34} width={90} radius={10} />
                    <Skeleton height={34} width={90} radius={10} />
                  </div>
                </div>
              ))}
            </>
          ) : sortedTrips.length === 0 ? (
            <p className={cx("no-data")}>Không có chuyến xe nào</p>
          ) : (
            sortedTrips.map((trip, index) => (
              <div key={trip.id} className={cx("trip-card")}>
                <div className={cx("card-info")}>
                  <span className={cx("card-label")}>STT: {index + 1}</span>
                  <span className={cx("card-value")}>ID: {trip.id}</span>
                  <span className={cx("card-value")}>
                    Tiêu Đề: {trip.title}
                  </span>
                  <span className={cx("card-value")}>
                    Điểm Đón: {trip.pickup_point}
                  </span>
                  <span className={cx("card-value")}>
                    Điểm Trả: {trip.dropoff_point}
                  </span>
                  <span className={cx("card-value")}>
                    Giá: {formatPrice(trip.price)}
                  </span>
                  <span className={cx("card-value")}>SĐT: {trip.phone}</span>
                  <span className={cx("card-value")}>
                    Mô Tả: {truncateDescription(trip.description)}
                  </span>
                  <span className={cx("card-value")}>
                    Tạo lúc: {formatDateTime(trip.created_at)}
                  </span>
                </div>
                <div className={cx("card-actions")}>
                  <button
                    onClick={() => setEditingTrip(trip)}
                    disabled={loading}
                    className={cx("button", "edit-button")}
                  >
                    <FontAwesomeIcon
                      icon={faEdit}
                      className={cx("button-icon")}
                    />
                    <span className={cx("button-text")}>Sửa</span>
                  </button>
                  <button
                    onClick={() => confirmDeleteTrip(trip)}
                    disabled={loading}
                    className={cx("button", "delete-button")}
                  >
                    <FontAwesomeIcon
                      icon={faTrash}
                      className={cx("button-icon")}
                    />
                    <span className={cx("button-text")}>Xóa</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default TripsManagement;